import React, { useState, useEffect, useMemo } from "react";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useTiersGameContext } from "../context/TiersGameContext";  
import { Box, Typography, MenuItem } from "@mui/material";
import QuickPickGamesComing from "../../QuickPickGames/CardPieces/QuickPickGamesComing";
import IntervalDropdown from "../../GamePageComponents/Dropdowns/IntervalDropdown";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { useGetGolfTournamentLeaderboard } from "../../../hooks/pools/useGetGolfTournamentLeaderboard";
import TournamentLeaderboard from "./TournamentLeaderboard";
import { DateTime } from 'luxon';
import { useSearchParams } from "react-router-dom";
import TabSelectionButtons from "../../GamePageComponents/TabSelectionButtons/TabSelectionButtons";
import TiersPlayerPicks from "./TiersPlayerPicks";
import TiersPicksByTier from "./TiersPicksByTier";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const TiersPoolPicksMainPage = () => {
    const {
        masterPoolData,
        interval,
        setInterval,
        intervalInfo,
        leaderboardData,
    } = useUserAndGameContext();

    const {
        firstInterval,
        lastInterval,
        theCurrentInterval,
        intervalsTournamentId,
        hasThisIntervalsDeadlinePassed,
    } = useTiersGameContext();

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Get the search parameters from the URL
    const [searchParams] = useSearchParams();
    
    // Initialize selectedTab from URL or default to 'leaderboard'
    const [selectedTab, setSelectedTab] = useState(
        searchParams.get('tab') || 'player-picks'
    );
    
    // Update selectedTab when URL parameter changes
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        if (tabParam) {
            setSelectedTab(tabParam);
        }
    }, [searchParams]);
    
    const tabs = masterPoolData?.picksPageInfo?.tabs;

    const gamesComingTitle = masterPoolData?.modalInfo?.pickStats?.title;
    const gamesComingBody = masterPoolData?.modalInfo?.pickStats?.body;

    
    
    // Initialize the mutation hook with full state destructuring
    const { 
        mutate: fetchTournamentLeaderboard, 
        data: tournamentLeaderboardData,
        isLoading,
        isError,
        error
    } = useGetGolfTournamentLeaderboard();
    
    // Trigger the mutation when tournament ID changes
    useEffect(() => {
        if (intervalsTournamentId) {
            fetchTournamentLeaderboard(intervalsTournamentId);
        }
    }, [intervalsTournamentId, fetchTournamentLeaderboard]);
    
    // Show loading screen if data is being fetched
    if (isLoading) {
        return <LoadingScreen />;
    }

    // Show error message if there was an error
    if (isError) {
        console.error("Error fetching tournament leaderboard:", error);
        // You can add an error UI component here if needed
    }

    const todayInNewYork = DateTime.now().setZone('America/New_York');
    const tournamentRounds = tournamentLeaderboardData?.Tournament?.Rounds;

    // Simple function to determine the current round number
    const getCurrentRoundNumber = () => {
        if (!tournamentRounds || tournamentRounds.length === 0) return 1;
        
        // Format today's date for comparison and ensure correct time zone
        const today = todayInNewYork.startOf('day');
        
        // Check if date is before all tournament days - return round 1
        const firstRoundDate = DateTime.fromISO(tournamentRounds[0].Day).startOf('day');
        if (today < firstRoundDate) return 1;
        
        // Check if date is after all tournament days - return round 4
        const lastRoundDate = DateTime.fromISO(tournamentRounds[tournamentRounds.length - 1].Day).startOf('day');
        if (today > lastRoundDate) return 4;
        
        // Alternative approach: compare by date string instead of DateTime object
        const todayStr = today.toFormat('yyyy-MM-dd');
        
        for (const round of tournamentRounds) {
            // Extract just the date part for comparison
            const roundDateStr = DateTime.fromISO(round.Day).toFormat('yyyy-MM-dd');
            
            if (todayStr === roundDateStr) {
                return round.Number;
            }
        }
        
        console.warn("Unexpected date scenario in tournament round calculation");
        return 1;
    };
    
    // Get current round number
    const currentRound = getCurrentRoundNumber();
    

    return (
        <Box>
            {hasThisIntervalsDeadlinePassed && (
                <TabSelectionButtons selectedTab={selectedTab} tabs={tabs} />
            )}

            {theCurrentInterval > firstInterval && (
                <IntervalDropdown
                    interval={interval}
                    setInterval={setInterval}
                    firstInterval={firstInterval}
                    lastInterval={lastInterval}
                    theCurrentInterval={theCurrentInterval}
                    intervalInfo={intervalInfo}
                    includeOverall={false}
                />
            )}

            {hasThisIntervalsDeadlinePassed && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px 8px',
                        gap: '8px',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: isMobile ? '24px' : '32px',
                            fontWeight: 700,
                            color: '#002129',
                        }}
                    >
                        {tournamentLeaderboardData?.Tournament?.Name}
                    </Typography>
                    <Typography
                        style={{
                            fontSize: isMobile ? '16px' : '20px',
                            fontWeight: 700,
                            color: '#002129',
                        }}
                    >
                        {tournamentLeaderboardData?.Tournament?.IsOver ? 'Final' : `Round ${currentRound}`}
                    </Typography>
                </Box>
            )}

            {selectedTab === 'leaderboard' && hasThisIntervalsDeadlinePassed && (
                <TournamentLeaderboard
                    tournamentLeaderboardData={tournamentLeaderboardData}
                    leaderboardData={leaderboardData}
                    interval={interval}
                    currentRound={currentRound}
                />
            )}

            {selectedTab === 'player-picks' && hasThisIntervalsDeadlinePassed && (
                <TiersPlayerPicks interval={interval}/>
            )}

            {selectedTab === 'tiers' && hasThisIntervalsDeadlinePassed && (
                <TiersPicksByTier
                    tournamentLeaderboardData={tournamentLeaderboardData}
                    leaderboardData={leaderboardData}
                    interval={interval}
                    currentRound={currentRound}
                />
            )}

            {!hasThisIntervalsDeadlinePassed && (
                <QuickPickGamesComing
                    page={'player-picks'}
                    title={gamesComingTitle}
                    body={gamesComingBody}
                    quickPick={false}
                />
            )}
        </Box>
    );
};

export default TiersPoolPicksMainPage;